import { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, X, Check, ChevronDown, Wand2, Plus,
  Zap, Briefcase, ShoppingBag, Megaphone, Share2, Feather,
  Headphones, Heart, Plane, GraduationCap, PartyPopper, Mic2,
} from "lucide-react";
import { type Preset, PRESETS, PRESET_CATEGORIES } from "@/lib/studio-presets";

interface FormatCommandProps {
  selectedPreset: Preset | null;
  pinnedPresets: string[];
  onSelectPreset: (p: Preset | null) => void;
  onOpenFormatPicker: () => void;
}

const POPULAR_LABELS = [
  "Social media post", "Follow-up email", "Instagram caption",
  "Twitter / X thread", "TikTok script", "Cold email",
  "Cover letter", "Thank you note", "Birthday message", "Newsletter",
  "LinkedIn DM", "YouTube script", "LinkedIn carousel", "How-to guide",
];

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  "Popular": Zap,
  "Work & Professional": Briefcase,
  "E-commerce & Listings": ShoppingBag,
  "Marketing & Ads": Megaphone,
  "Social Media Content": Share2,
  "Creative & Writing": Feather,
  "Customer & Support": Headphones,
  "Health & Lifestyle": Heart,
  "Food & Travel": Plane,
  "School & Learning": GraduationCap,
  "Personal & Life": PartyPopper,
  "Speaking & Stage": Mic2,
};

const ALL_CATEGORIES = [
  { label: "Popular", presets: POPULAR_LABELS },
  ...PRESET_CATEGORIES,
];

export function FormatCommand({
  selectedPreset,
  pinnedPresets,
  onSelectPreset,
  onOpenFormatPicker,
}: FormatCommandProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("Popular");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 80);
    } else {
      setSearch("");
      setActiveCategory("Popular");
    }
  }, [isOpen]);

  const activeCategoryPresets = useMemo(() => {
    const cat = ALL_CATEGORIES.find((c) => c.label === activeCategory);
    return PRESETS.filter((p) => cat?.presets.includes(p.label));
  }, [activeCategory]);

  const searchResults = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase().trim();
    return PRESETS.filter(
      (p) =>
        p.label.toLowerCase().includes(q) ||
        p.hint.toLowerCase().includes(q)
    );
  }, [search]);

  const isSearching = search.trim().length > 0;
  const displayPresets = isSearching ? searchResults : activeCategoryPresets;

  const handleSelect = (preset: Preset) => {
    onSelectPreset(preset);
    setIsOpen(false);
  };

  const pinnedPresetObjects = PRESETS.filter((p) => pinnedPresets.includes(p.label));

  return (
    <div className="mb-4" ref={containerRef}>
      {/* Selector button row */}
      <div className="relative mb-2">
        <div className="flex items-center gap-2">
          {/* Main trigger + clear button (siblings, not nested) */}
          <div className="flex-1 relative flex items-stretch">
            <button
              type="button"
              onClick={() => setIsOpen((v) => !v)}
              className={`flex-1 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border text-sm font-medium text-left transition-all duration-200 ${
                selectedPreset
                  ? "border-cyan-500/40 bg-cyan-500/8 text-cyan-300 pr-[4.5rem]"
                  : "border-border/40 bg-secondary/20 text-muted-foreground hover:border-border/70 hover:bg-secondary/40 hover:text-foreground"
              }`}
              style={
                selectedPreset
                  ? {
                      boxShadow:
                        "0 0 0 1px hsl(188 90% 42% / 0.15), 0 0 20px hsl(188 90% 42% / 0.08)",
                    }
                  : undefined
              }
            >
              {selectedPreset ? (
                <>
                  {(() => {
                    const Icon = selectedPreset.icon;
                    return <Icon className="w-4 h-4 shrink-0 text-cyan-400" />;
                  })()}
                  <span className="flex-1 font-semibold text-cyan-300 truncate">
                    {selectedPreset.label}
                  </span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 shrink-0 text-cyan-400/50 transition-transform duration-200 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 shrink-0" />
                  <span className="flex-1">Choose a format…</span>
                  <ChevronDown
                    className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </>
              )}
            </button>

            {/* Clear button — absolute sibling, never nested inside the trigger */}
            {selectedPreset && (
              <button
                type="button"
                onClick={() => onSelectPreset(null)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-cyan-500/20 transition-colors"
                aria-label="Clear format"
              >
                <X className="w-3.5 h-3.5 text-cyan-400/60 hover:text-cyan-300" />
              </button>
            )}
          </div>

          {/* Pin button */}
          <button
            type="button"
            onClick={onOpenFormatPicker}
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-border/30 bg-secondary/20 text-muted-foreground text-xs font-semibold hover:border-border/60 hover:bg-secondary/40 hover:text-foreground transition-all duration-200 shrink-0"
            title="Pin formats to quick-access"
          >
            <Plus className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Pin</span>
          </button>
        </div>

        {/* Mega-dropdown */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              key="format-command-dropdown"
              initial={{ opacity: 0, scale: 0.97, y: -6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: -6 }}
              transition={{ duration: 0.14, ease: "easeOut" }}
              className="absolute left-0 top-full mt-1.5 z-50 bg-card border border-cyan-500/20 rounded-2xl overflow-hidden flex flex-col"
              style={{
                width: "min(480px, calc(100vw - 2rem))",
                maxHeight: "460px",
                boxShadow:
                  "0 0 0 1px hsl(188 90% 42% / 0.12), 0 24px 56px rgba(0,0,0,0.45)",
              }}
            >
              {/* Search bar */}
              <div className="px-3 py-2.5 border-b border-border/30 shrink-0">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/40 pointer-events-none" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search 130+ formats…"
                    className="w-full pl-8 pr-8 py-2 rounded-lg border border-border/30 bg-secondary/30 text-sm text-foreground placeholder:text-muted-foreground/35 focus:outline-none focus:ring-1 focus:ring-cyan-500/40 focus:border-cyan-500/40 transition-all"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-foreground transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Body: sidebar + list */}
              <div className="flex flex-1 min-h-0">
                {/* Left category sidebar (hidden during search) */}
                {!isSearching && (
                  <div
                    className="w-[138px] shrink-0 border-r border-border/20 bg-secondary/15 overflow-y-auto py-1.5"
                    style={{ scrollbarWidth: "thin" }}
                  >
                    {ALL_CATEGORIES.map((cat) => {
                      const CatIcon = CATEGORY_ICONS[cat.label] ?? Wand2;
                      const isActive = activeCategory === cat.label;
                      return (
                        <button
                          key={cat.label}
                          type="button"
                          onClick={() => setActiveCategory(cat.label)}
                          className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left transition-all border-l-2 ${
                            isActive
                              ? "bg-cyan-500/10 text-cyan-300 border-cyan-500"
                              : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground border-transparent"
                          }`}
                        >
                          <CatIcon
                            className={`w-3 h-3 shrink-0 ${
                              isActive ? "text-cyan-400" : ""
                            }`}
                          />
                          <span className="text-[11px] font-semibold leading-tight">
                            {cat.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Right formats list */}
                <div
                  className="flex-1 overflow-y-auto py-1.5"
                  style={{ scrollbarWidth: "thin" }}
                >
                  {isSearching && searchResults.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <p className="text-xs text-muted-foreground/50">
                        No formats match "{search}"
                      </p>
                    </div>
                  ) : (
                    displayPresets.map((preset) => {
                      const Icon = preset.icon;
                      const isActive = selectedPreset?.label === preset.label;
                      return (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => handleSelect(preset)}
                          className={`w-full flex items-start gap-3 px-3 py-2.5 text-left transition-all ${
                            isActive
                              ? "bg-cyan-500/10 text-cyan-300"
                              : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
                          }`}
                          style={
                            isActive
                              ? { boxShadow: "inset 0 0 0 1px rgba(6,182,212,0.28)" }
                              : undefined
                          }
                        >
                          <div
                            className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${
                              isActive ? "bg-cyan-500/15" : "bg-secondary/60"
                            }`}
                          >
                            <Icon
                              className={`w-3 h-3 ${
                                isActive ? "text-cyan-400" : "text-muted-foreground"
                              }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-xs font-semibold leading-tight ${
                                isActive ? "text-cyan-300" : "text-foreground"
                              }`}
                            >
                              {preset.label}
                            </p>
                            <p className="text-[10px] text-muted-foreground/50 leading-snug mt-0.5 truncate">
                              {preset.hint.replace(/^e\.g\.\s*/i, "")}
                            </p>
                          </div>
                          {isActive && (
                            <Check className="w-3.5 h-3.5 shrink-0 mt-1 text-cyan-400" />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-3 py-2 border-t border-border/20 bg-secondary/10 flex items-center justify-between shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    onOpenFormatPicker();
                  }}
                  className="text-[10px] font-medium text-cyan-400/60 hover:text-cyan-400 transition-colors flex items-center gap-1"
                >
                  <Plus className="w-2.5 h-2.5" /> Pin to quick-access
                </button>
                {selectedPreset && (
                  <button
                    type="button"
                    onClick={() => {
                      onSelectPreset(null);
                      setIsOpen(false);
                    }}
                    className="text-[10px] text-muted-foreground/40 hover:text-foreground transition-colors"
                  >
                    Clear format
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Quick-access pinned chips */}
      {pinnedPresetObjects.length > 0 && (
        <div
          className="flex items-center gap-1.5 overflow-x-auto pb-0.5"
          style={{ scrollbarWidth: "none" }}
        >
          {pinnedPresetObjects.map((preset) => {
            const Icon = preset.icon;
            const isActive = selectedPreset?.label === preset.label;
            return (
              <button
                key={preset.label}
                type="button"
                onClick={() => onSelectPreset(preset)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium whitespace-nowrap shrink-0 transition-all duration-200 ${
                  isActive
                    ? "border-cyan-500/50 bg-cyan-500/10 text-cyan-300"
                    : "border-border/40 bg-secondary/20 text-muted-foreground hover:border-border/70 hover:bg-secondary/50 hover:text-foreground"
                }`}
                style={
                  isActive
                    ? {
                        boxShadow:
                          "0 0 0 1px hsl(188 90% 42% / 0.5), 0 0 16px hsl(188 90% 42% / 0.15)",
                      }
                    : undefined
                }
              >
                <Icon className="w-3 h-3 shrink-0" />
                {preset.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={onOpenFormatPicker}
            className="text-[10px] text-muted-foreground/40 hover:text-cyan-400 font-medium transition-colors ml-1 shrink-0"
          >
            Edit pins
          </button>
        </div>
      )}
    </div>
  );
}
